import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { ButtonLoader } from 'src/components/forms/ButtonLoader';
import { Col, Row } from 'reactstrap';
import TextInput from 'src/components/forms/TextInput';
import styles from "./chatbot.module.css";
import DownloadIcon from '@mui/icons-material/Download';
import Toast from 'src/components/custom/Toast';
import Axios from 'src/utils/axios';
import ErrorMsg from "../../components/custom/ErrorMsg";
import * as Yup from "yup";
import { useFormik } from "formik";

const size = 250;
const logoUrl = 'https://jinni-bot-storage.s3.amazonaws.com/leads/black-logo2_1723205484990.jpg';

const QRCodeComponent = ({ getUserListData, userdata }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isLoadingQr, setIsLoadingQr] = useState(false);
  const canvasRef = useRef(null);

  const validationSchema = Yup.object({
    url: Yup.string()
    .test("is-valid-url", "Invalid URL format.", (value) => {
      if (!value) return false; // If value is empty, fail validation

      // Check if URL has www or not
      if (!/^(?:https?:\/\/)?(?:www\.)?[\w-]+(?:\.[a-z]{2,})(?:\.[a-z]{2,})?(?:\.[a-z]{2,})?(?:\.[a-z]{2,})?(?:\.[a-z]{2,})?$/i.test(value)) {
        return false;
      }
      return true; // Passes all validations
    })
    .required("URL is required."),
  });

  const formik = useFormik({
    initialValues: {
      url : userdata?.qrCodeUrl || ""
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoadingQr(true);
    try {
      const updateData = await Axios.post(`/whatsapp/generateQrCode`, { qrCodeUrl: values?.url });
      if (updateData.data.success) {
        getUserListData();
        setQrCodeUrl(await QRCode.toDataURL(values?.url, { width: size }));
        Toast(updateData.data.message, 'success');
      } else {
        Toast(updateData.data.message, 'error');
      }
    } catch (error) {
      Toast('Error generating QR code:', 'error');
    } finally {
      setIsLoadingQr(false);
    }
    },
  });

  // Generate QR code URL on URL change or userdata.qrCodeUrl change
  useEffect(() => {
    if (userdata?.qrCodeUrl) {
      QRCode.toDataURL(userdata?.qrCodeUrl, { width: size })
        .then(dataUrl => setQrCodeUrl(dataUrl))
        .catch(err => console.error('Error generating QR code URL:', err));
    }
  }, [userdata?.qrCodeUrl]);

  // Draw QR code and logo on the canvas
  useEffect(() => {
    const drawQRCodeWithLogo = async () => {
      if (!qrCodeUrl) return;
  
      const canvas = canvasRef.current;
      if (!canvas) return;
  
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
  
      const qrImage = new Image();
      qrImage.crossOrigin = 'anonymous'; 
      qrImage.src = qrCodeUrl;
  
      const logoImage = new Image();
      logoImage.crossOrigin = 'anonymous';
      logoImage.src = logoUrl;
  
      try {
        await Promise.all([
          new Promise((resolve, reject) => {
            qrImage.onload = resolve;
            qrImage.onerror = reject;
          }),
          new Promise((resolve, reject) => {
            logoImage.onload = resolve;
            logoImage.onerror = reject;
          })
        ]);
  
        canvas.width = size;
        canvas.height = size;
  
        // Draw the QR code image
        ctx.drawImage(qrImage, 0, 0, size, size);
  
        // Draw the logo image
        const logoSize = size / 6; // Adjust size as needed
        const logoX = (size - logoSize) / 2;
        const logoY = (size - logoSize) / 2;
  
        ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
  
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };
  
    drawQRCodeWithLogo();
  }, [qrCodeUrl, logoUrl]);
  

  const handleDownloadQRCode = () => {
    if (qrCodeUrl) {
      // Create a new canvas to draw the QR code and logo
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
  
      if (!ctx) return;
  
      const qrImage = new Image();
      qrImage.crossOrigin = 'anonymous';
      qrImage.src = qrCodeUrl;
  
      const logoImage = new Image();
      logoImage.crossOrigin = 'anonymous';
      logoImage.src = logoUrl;
  
      // Load images and draw them on the canvas
      qrImage.onload = () => {
        ctx.drawImage(qrImage, 0, 0, size, size);
  
        logoImage.onload = () => {
          const logoSize = size / 6; // Adjust size as needed
          const logoX = (size - logoSize) / 2;
          const logoY = (size - logoSize) / 2;
  
          // Draw logo with full opacity
          ctx.globalAlpha = 1.0; // Ensure the logo is fully opaque
          ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
  
          // Convert canvas to data URL and trigger download
          const dataUrl = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = 'qrcode.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };
  
        logoImage.onerror = () => console.error('Error loading logo image');
      };
  
      qrImage.onerror = (error) => console.error('Error loading QR code image:', error);
    }
  };
  
  return (
    <div>
      <div>
        <div className="border-bottom row align-items-center">
          <div className="col-6">
            <h5 className={styles.profile_title}>Generate QR</h5>
            <p className={styles.profile_subtitle}>
              Generate your QR code.
            </p>
          </div>
          <div className="col-6 mb-2" style={{ textAlign: 'end' }}>
            <ButtonLoader
              onClickhandle={handleDownloadQRCode}
              buttonName={"Download QR"}
              loaderColor={"white"}
              style={{ paddingLeft: '12px', paddingRight: '12px' }}
              className={styles.profile_btn}
              icon={<DownloadIcon sx={{ fontSize: '18px' }} />}
              disabled={!qrCodeUrl}
            />
          </div>
        </div>

        <Row className="mt-3">
          <Col sm="12" md="6" style={{ borderRight: '1px solid #ddd' }}>
            <div style={{ width: '80%' }}>
              <label className={styles.custom_label + ' mb-0'}>Enter URL</label>
              <TextInput
                customClass={styles.custom_field + ' w-100 '}
                type="text"
                name="url"
                value={formik.values.url}
                onChange={formik.handleChange}
                variant="outlined"
                placeholder="URL"
                size={'small'}
              />
              <ErrorMsg hasError={formik.touched.url && formik.errors.url}>{formik.errors.url}</ErrorMsg>
            </div>
            <div className="row">
              <div className="col pt-3">
                <ButtonLoader
                  onClickhandle={formik.handleSubmit}
                  buttonName={"Generate QR"}
                  loaderColor={"white"}
                  style={{ paddingLeft: '12px', paddingRight: '12px' }}
                  className={styles.profile_btn}
                  loading={isLoadingQr}
                />
              </div>
            </div>
          </Col>
          <Col sm="12" md="6" style={{ textAlign: 'center' }}>
            
            {formik.values.url ?  <canvas ref={canvasRef} /> : 
              <p className="d-flex align-items-center justify-content-center w-100 h-100" style={{color:"#898c8f"}}>QR code is not available.</p>
            }
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default QRCodeComponent;
